import { Box, Grid, Heading, smBumps, xsBumps } from '@wrisk/ui-components'
import React from 'react'

import { Quote } from '../../../domain'
import { useCoverLevelOption } from '../../../hooks/coverOption'
import { useConditionalCovers, useIncludedCovers } from '../../../hooks/covers'
import {
  tProduct,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { getExposuresContentBaseUrl, useConfig } from '../../../state/configuration'
import { useProduct } from '../../product/ProductProvider'
import { ConditionalCoverItem } from './ConditionalCoverItem'
import { IncludedCoverItem } from './IncludedCoverItem'

interface IncludedCoverItemsProps {
  quote: Quote
}

export const IncludedCoverItems: React.FC<IncludedCoverItemsProps> = ({ quote }) => {
  const { t } = useWriskTranslation()

  const { scheme, product } = useProduct()
  const { coverLevelOption } = useCoverLevelOption(quote, product)

  const includedCovers = useIncludedCovers(product, coverLevelOption)
  const conditionalCovers = useConditionalCovers(quote, product)

  const contentBaseUrl = useConfig(getExposuresContentBaseUrl(scheme.schemeCode))

  const includedCoverItems = includedCovers?.flatMap((cover, index, covers) => {
    return typeof cover === 'string' ? (
      <IncludedCoverItem
        key={cover}
        width={1}
        cover={cover}
        contentBaseUrl={contentBaseUrl}
      />
    ) : (
      [
        <Heading typoSize='md' px={smBumps} py={xsBumps} key={`${cover.header}-header`}>
          {t(tProduct('cover-levels', cover.header, 'header'))}
        </Heading>,
        ...cover.items.map((item) => (
          <IncludedCoverItem
            key={`${cover.header}-${item}`}
            width={1}
            cover={item}
            contentBaseUrl={contentBaseUrl}
          />
        )),
        index < covers.length - 1 ? (
          <Box my={2} px={smBumps} width={1} key={`${cover.header}-divider`}>
            <Box height={0} borderTopWidth={1}>
              &nbsp;
            </Box>
          </Box>
        ) : null,
      ]
    )
  })

  const conditionalCoverItems = conditionalCovers.map((cover) => (
    <ConditionalCoverItem key={cover.coverCode} width={1} conditionalCover={cover} />
  ))

  return (
    <Grid width={1} p={2} alignItems='stretch'>
      {includedCoverItems}
      {conditionalCoverItems}
    </Grid>
  )
}
